import { escapeHtml } from '../../utils/format';
import { formatBoxes } from './productionUtils';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Printable A4 MDF cutting list. `boards` is photoBlockCuttingList() output; `context`
// is a short line describing what the list covers (e.g. the customer filter).
export const buildCuttingListDocument = (boards, context) => {
  const summaryHtml = boards.map(board => `
    <div class="pull">
      <div class="pull-sheets">${board.sheets}</div>
      <div class="pull-label">${board.sheets === 1 ? 'sheet' : 'sheets'} to pull</div>
      <div class="pull-board">${escapeHtml(board.name)}</div>
    </div>`).join('');

  const boardHtml = boards.map(board => {
    const rows = board.rows.map(row => `
          <tr>
            <td class="size">${escapeHtml(row.size)}</td>
            <td class="num">${row.blocks.toLocaleString()}</td>
            <td class="num">${escapeHtml(formatBoxes(row.fullBoxes, row.loose))}</td>
            <td class="num sheets">${row.sheets}</td>
            <td class="wos">${escapeHtml(row.serialNames.join(', '))}</td>
            <td class="tick"><span class="box"></span></td>
          </tr>`).join('');
    return `
    <section class="board">
      <div class="board-header">
        <h2>${escapeHtml(board.name)}</h2>
        <span class="code">${escapeHtml(board.code)}</span>
      </div>
      <table>
        <colgroup>
          <col class="c-size"><col class="c-num"><col class="c-boxes"><col class="c-num"><col><col class="c-tick">
        </colgroup>
        <thead>
          <tr>
            <th>Size</th>
            <th class="num">Blocks</th>
            <th class="num">Boxes</th>
            <th class="num">Sheets</th>
            <th>Work orders</th>
            <th class="tick">Cut</th>
          </tr>
        </thead>
        <tbody>${rows}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td class="num">${board.blocks.toLocaleString()}</td>
            <td class="num">${escapeHtml(formatBoxes(board.fullBoxes, board.loose))}</td>
            <td class="num sheets">${board.sheets}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
      </table>
    </section>`;
  }).join('');

  const totalSheets = boards.reduce((sum, board) => sum + board.sheets, 0);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>MDF cutting list</title>
  <style>
    @page { size: A4; margin: 14mm 14mm 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Helvetica Neue", Arial, sans-serif;
      color: #1a2233;
      font-size: 10.5pt;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .doc-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding-bottom: 4mm;
      border-bottom: 2.5pt solid #243660;
    }
    h1 { margin: 0; font-size: 20pt; letter-spacing: -0.2pt; color: #243660; }
    .subtitle { margin-top: 1mm; font-size: 10pt; color: #5b6475; }
    .date { text-align: right; font-size: 10pt; color: #5b6475; line-height: 1.5; }
    .date strong { display: block; font-size: 12pt; color: #1a2233; }

    .summary { display: flex; gap: 5mm; margin: 6mm 0 2mm; }
    .pull {
      flex: 1;
      padding: 3.5mm 5mm;
      border: 1pt solid #cdd5e3;
      border-left: 4pt solid #243660;
      border-radius: 2mm;
      background: #f3f6fb;
    }
    .pull-sheets { font-size: 26pt; font-weight: 700; line-height: 1; color: #243660; }
    .pull-label { margin-top: 1mm; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.6pt; color: #5b6475; }
    .pull-board { margin-top: 1.5mm; font-size: 10.5pt; font-weight: 600; }

    .board { margin-top: 7mm; page-break-inside: avoid; }
    .board-header { display: flex; align-items: baseline; gap: 3mm; margin-bottom: 2mm; }
    h2 { margin: 0; font-size: 13pt; }
    .code { font-family: Menlo, Consolas, monospace; font-size: 8.5pt; color: #6b7487; }

    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    col.c-size { width: 16%; }
    col.c-num { width: 12%; }
    col.c-boxes { width: 20%; }
    col.c-tick { width: 9%; }
    th, td { padding: 2.6mm 3mm; text-align: left; vertical-align: middle; }
    thead th {
      background: #243660;
      color: #fff;
      font-size: 8.5pt;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5pt;
    }
    tbody td { border-bottom: 0.75pt solid #d9dee8; }
    tbody tr:nth-child(even) td { background: #f7f9fc; }
    .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .size { font-weight: 700; font-size: 12pt; }
    .sheets { font-weight: 700; font-size: 12pt; }
    .wos { font-size: 8.5pt; color: #4a5366; line-height: 1.4; }
    .tick { text-align: center; }
    .box { display: inline-block; width: 5mm; height: 5mm; border: 1pt solid #8a93a6; border-radius: 1mm; }
    tfoot td {
      border-top: 1.5pt solid #243660;
      background: #e8edf6;
      font-weight: 700;
    }

    .doc-footer {
      margin-top: 8mm;
      padding-top: 2mm;
      border-top: 0.75pt solid #d9dee8;
      display: flex;
      justify-content: space-between;
      font-size: 8.5pt;
      color: #8a93a6;
    }
  </style>
</head>
<body>
  <header class="doc-header">
    <div>
      <h1>MDF Cutting List</h1>
      <div class="subtitle">Photo Blocks · ${escapeHtml(context)}</div>
    </div>
    <div class="date">
      <strong>${escapeHtml(new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }))}</strong>
      ${escapeHtml(plural(totalSheets, 'sheet', 'sheets'))} in total
    </div>
  </header>

  <div class="summary">${summaryHtml}
  </div>
  ${boardHtml}

  <footer class="doc-footer">
    <span>Sheets are rounded up per size · 2440 × 1220 × 25mm MDF</span>
    <span>Cut by ____________________</span>
  </footer>
</body>
</html>`;
};
